import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { useSelector, useDispatch } from "react-redux";
import {
  get_wishlist_products,
  remove_wishlist,
  messageClear,
  add_to_card,
  delete_card_product,
  get_cart_products
} from "../store/reducers/cardReducer";
import { fetchStoreBySlug } from "../store/reducers/homeReducer";
import toast from "react-hot-toast";
import { Heart, Package, ChevronRight, ShoppingBag, Minus } from "lucide-react";
import Ratings from "../components/Ratings";
import { formatCurrency, fetchExchangeRates } from "../utils/currencyUtils";
import axios from "../api/api";
import { Button } from "../components/ui/button";
import { areStoreNamesEqual, normalizeStoreName } from "../utils/storeUtils";

const FadeInItem = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out transform ${isVisible
        ? "opacity-100 translate-y-0 brightness-100 grayscale-0"
        : "opacity-0 translate-y-12 brightness-50 grayscale"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Wishlist = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const { wishlist, successMessage, card_products } = useSelector((state) => state.card);
  const { seller } = useSelector((state) => state.home);
  const [currencyCode, setCurrencyCode] = useState("NGN");
  const [exchangeLoaded, setExchangeLoaded] = useState(false);

  const { storeName: urlStoreName } = useParams();
  const isStoreMode = !!urlStoreName;
  const storeToPass = urlStoreName || location.state?.fromStore;

  const filteredWishlist = storeToPass
    ? wishlist.filter(item => areStoreNamesEqual(item.shopName, storeToPass))
    : wishlist;

  const isProductInCart = (productId) => {
    if (!card_products) return false;
    // Check if card_products is array of groups (backend standard) or flat array (local storage guest sometimes)
    // Based on reducer, it seems to always try to conform to structure, but let's be safe.
    // Reducer 'get_cart_products' maps local storage to grouped structure too.
    return card_products.some(shop =>
      shop.products.some(item => item.productInfo._id === productId)
    );
  };

  const add_card = (id) => {
    const product = wishlist.find(item => item.productId === id);
    if (!product) return;

    // Check if product is already in cart
    const cartItemId = card_products?.flatMap(shop => shop.products).find(item => item.productInfo._id === id)?._id;

    if (cartItemId) {
      dispatch(delete_card_product({
        card_id: cartItemId,
        storeName: product.shopName || storeToPass || null
      }));
    } else {
      // Add to cart - allow guest checkout (userId: null)
      dispatch(add_to_card({
        userId: userInfo ? userInfo.id : null,
        quantity: 1,
        productId: id,
        productInfo: {
          _id: product.productId,
          name: product.name,
          price: product.price,
          images: [product.image],
          discount: product.discount,
          shopName: product.shopName,
          slug: product.slug,
          currency: product.currency,
          rating: product.rating,
          sellerId: product.sellerId || seller?._id // Use seller._id from context if sellerId not in wishlist
        },
        storeName: product.shopName || null
      }));
    }
  };

  useEffect(() => {
    // Only fetch store if NOT in StoreLayout. StoreLayout handles it otherwise.
    if (storeToPass && !isStoreMode) {
      dispatch(fetchStoreBySlug(storeToPass));
    }
  }, [storeToPass, dispatch, isStoreMode]);

  // ... (exchange rate effect)
  // ... (user details effect)
  // ... (get products effect)
  useEffect(() => {
    if (successMessage) {
      dispatch(messageClear());
      // Refresh cart and wishlist data
      const params = storeToPass
        ? { userId: userInfo ? userInfo.id : null, storeName: storeToPass }
        : (userInfo ? userInfo.id : null);

      dispatch(get_cart_products(params));
      dispatch(get_wishlist_products(params));
    }
  }, [successMessage, userInfo, storeToPass, dispatch]);

  // Theme logic - Only if NOT StoreMode
  const getThemeClass = () => {
    if (isStoreMode) return '';

    const themeSettings = seller?.themeSettings;
    const useCustomTheme = themeSettings?.useCustomTheme;

    // Custom theme always uses light-theme class (defaults to light colors)
    if (useCustomTheme) {
      return 'light-theme';
    }
    if (seller?.shopInfo?.theme === 'light') {
      return 'light-theme';
    }
    if (storeToPass) {
      const normalizedStore = normalizeStoreName(storeToPass);
      const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
      if (cachedTheme === 'light') return 'light-theme';

      try {
        const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
        if (cachedSettings) {
          const settings = JSON.parse(cachedSettings);
          if (settings.useCustomTheme) return 'light-theme';
        }
      } catch (e) { }
    }
    return '';
  };
  const themeClass = getThemeClass();

  // Apply theme class to body immediately on render if we have cached theme context
  // Only if NOT StoreMode
  if (!isStoreMode && themeClass === 'light-theme') {
    document.body.classList.add('light-theme');

    // If we have cached custom theme settings, apply them now to prevent flash
    if (storeToPass) {
      try {
        const normalizedStore = normalizeStoreName(storeToPass);
        const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
        if (cachedSettings) {
          const settings = JSON.parse(cachedSettings);
          if (settings.useCustomTheme) {
            const colors = settings.colors || {};
            const setVar = (name, value) => {
              if (value) document.body.style.setProperty(name, value);
            };
            const getContrastColor = (hexcolor) => {
              if (!hexcolor || hexcolor === 'transparent') return null;
              const hex = hexcolor.replace('#', '');
              const r = parseInt(hex.slice(0, 2), 16);
              const g = parseInt(hex.slice(2, 4), 16);
              const b = parseInt(hex.slice(4, 6), 16);
              const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
              return (yiq >= 128) ? '#000000' : '#ffffff';
            };
            const contrastColor = getContrastColor(colors.background);
            setVar('--store-bg', colors.background);
            setVar('--store-text', contrastColor);
            setVar('--store-icon', contrastColor);
            setVar('--store-button-bg', colors.buttonBg);
            setVar('--store-button-text', colors.buttonText);
            setVar('--store-product-card-bg', colors.productCardBg);
            setVar('--store-header-bg', colors.headerBg);
            setVar('--store-footer-bg', colors.footerBg);
            setVar('--store-border', colors.border);
            if (settings.typography?.fontFamily) {
              const fontFamily = settings.typography.fontFamily;
              const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
              if (!document.getElementById(fontId)) {
                const link = document.createElement('link');
                link.id = fontId;
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                document.head.appendChild(link);
              }
              setVar('--store-font-family', `"${fontFamily}", sans-serif`);
            }
          }
        }
      } catch (e) {
        console.error("Error applying cached theme:", e);
      }
    }
  }

  // Theme effect - Only if NOT StoreMode
  useEffect(() => {
    if (isStoreMode) return;

    if (seller?.shopInfo && storeToPass) {
      const normalizedStore = normalizeStoreName(storeToPass);
      localStorage.setItem(`store-theme-${normalizedStore}`, seller.shopInfo.theme);
      localStorage.setItem(`store-settings-${normalizedStore}`, JSON.stringify(seller.themeSettings || {}));

      const themeSettings = seller?.themeSettings;
      const useCustomTheme = themeSettings?.useCustomTheme;

      // Apply light-theme class when using custom theme or when theme is 'light'
      if (useCustomTheme || seller.shopInfo.theme === 'light') {
        document.body.classList.add('light-theme');
      } else {
        document.body.classList.remove('light-theme');
      }

      // Apply custom CSS variables when useCustomTheme is true
      if (useCustomTheme && themeSettings) {
        const colors = themeSettings.colors || {};
        const setVar = (name, value) => {
          if (value) document.body.style.setProperty(name, value);
        };

        const getContrastColor = (hexcolor) => {
          if (!hexcolor || hexcolor === 'transparent') return null;
          const hex = hexcolor.replace('#', '');
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
          return (yiq >= 128) ? '#000000' : '#ffffff';
        };

        const contrastColor = getContrastColor(colors.background);
        setVar('--store-bg', colors.background);
        setVar('--store-text', contrastColor);
        setVar('--store-icon', contrastColor);
        setVar('--store-button-bg', colors.buttonBg);
        setVar('--store-button-text', colors.buttonText);
        setVar('--store-product-card-bg', colors.productCardBg);
        setVar('--store-header-bg', colors.headerBg);
        setVar('--store-footer-bg', colors.footerBg);
        setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
        setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
        setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
        setVar('--store-input-focus', themeSettings.inputs?.focusRing || '#000000');
        setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
        setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
        setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
        setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
        setVar('--store-select-focus', themeSettings.selects?.focusRing || '#000000');
        setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
        setVar('--store-border', colors.border);
        setVar('--store-muted', colors.muted);
        setVar('--store-glass', colors.glass);

        // Load and apply Google Font
        if (themeSettings.typography?.fontFamily) {
          const fontFamily = themeSettings.typography.fontFamily;
          const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

          if (!document.getElementById(fontId)) {
            const link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
            document.head.appendChild(link);
          }

          setVar('--store-font-family', `"${fontFamily}", sans-serif`);
          document.body.style.fontFamily = `"${fontFamily}", sans-serif`;
        }
      }
    }

    // Cleanup when unmounting or changing stores
    return () => {
      document.body.classList.remove('light-theme');
    };
  }, [seller, storeToPass, isStoreMode]);

  return (
    <div className={`min-h-screen bg-[var(--store-bg)] transition-colors duration-300 ${!isStoreMode ? themeClass : ''}`}>
      {!isStoreMode && <Headers />}
      <div
        className="h-[220px] bg-cover bg-no-repeat relative bg-left bg-[var(--store-bg)]"
        style={{
          backgroundImage: themeClass === 'light-theme' ? 'none' : `url(${process.env.REACT_APP_URL}/images/banner/cartnew.jpg)`,
        }}
      >
        <div className={`absolute left-0 top-0 w-full h-full transition-colors duration-500 ${themeClass === 'light-theme' ? 'bg-[#0000004d]' : 'bg-[#0000008a]'}`}>
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
              <h2 className="text-3xl font-bold">Wishlist</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--store-product-card-bg)] py-5 mb-5 transition-colors duration-300">
        <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
          <div className="flex justify-start items-center text-md text-[var(--store-muted)] w-full">
            <Link to={location.state?.fromStore ? `/${location.state.fromStore}` : "/"}>Home</Link>
            <span className="">
              <ChevronRight size={18} />
            </span>
            <span className="text-[var(--store-text)]">Wishlist</span>
          </div>
        </div>
      </div>

      <section className="py-16 bg-[var(--store-bg)]">
        <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
          {filteredWishlist.length === 0 ? (
            <div className="w-full h-[400px] flex flex-col items-center justify-center gap-4">
              <div className="text-center text-[var(--store-muted)]">
                <Package size={64} className="mx-auto mb-4 opacity-50" />
                <p className="text-xl font-semibold">Your wishlist is empty</p>
                <p className="text-sm mt-2">Browse products and add your favorites!</p>
              </div>
              <Link to={location.state?.fromStore ? `/${location.state.fromStore}` : "/products"}>
                <Button className="bg-[var(--store-button-bg)] text-[var(--store-button-text)] hover:bg-[var(--store-button-bg)] hover:opacity-90">Browse Products</Button>
              </Link>
            </div>
          ) : (
            <div className="w-full grid grid-cols-4 md-lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-2 gap-6 sm:gap-2">
              {filteredWishlist.map((p, i) => (
                <FadeInItem key={i} delay={(i % 6) * 150}>
                  <div
                    className="bg-[var(--store-product-card-bg)] group transition-all duration-500 hover:shadow-md cursor-pointer"
                  >
                    <div className="relative overflow-hidden">
                      {isProductInCart(p.productId) && (
                        <div className={`flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-[#FFE11B] font-semibold text-xs right-2 z-10 shadow-lg transition-all duration-500 top-2`}>
                          <ShoppingBag size={20} className="text-black" />
                        </div>
                      )}
                      {p.discount ? (
                        <div className="flex justify-center items-center absolute text-white py-[2px] px-6 rounded-full bg-red-600 font-semibold text-s left-2 top-2">
                          {p.discount}%
                        </div>
                      ) : (
                        ""
                      )}
                      <Link to={p.shopName ? `/${p.shopName}/product/details/${p.productId}` : `/product/details/${p.productId}`} state={{ fromStore: storeToPass }} className="">
                        <img
                          className="sm:w-full w-full h-[240px] nex_img_cover"
                          src={p.image}
                          alt={p.name}
                        />
                      </Link>
                    </div>

                    <div className="py-3 text-[var(--store-text)] px-2">
                      <div className="p-2 flex justify-between items-center">
                        <span className="text-lg sm:text-base font-bold"> {formatCurrency(p.price, currencyCode, p.currency)}</span>
                      </div>
                      <div className="p-2">
                        <h2 className="sm:text-sm truncate">{p.name}</h2>
                      </div>
                      <div className="gap-3 p-2">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex">
                            <Ratings ratings={p.rating} />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => add_card(p.productId)}
                            className={`${isProductInCart(p.productId) ? 'bg-[var(--store-button-bg)] text-[var(--store-button-text)]' : 'bg-[var(--store-button-bg)] text-[var(--store-button-text)]'} flex-1 justify-center flex items-center nex_button_main font-semibold hover:opacity-90 sm:text-sm sm:py-1`}
                          >
                            {isProductInCart(p.productId) ? 'Remove' : 'Add to cart'}
                          </button>
                          <div onClick={() => dispatch(remove_wishlist({ wishlistId: p._id, storeName: storeToPass }))} className="w-[38px] h-[38px] cursor-pointer rounded-full flex justify-center items-center bg-[var(--store-glass)] backdrop-blur-md border border-[var(--store-border)] hover:bg-[var(--store-glass)] transition-all">
                            <Heart size={20} className="fill-red-500 text-red-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInItem>
              ))}
            </div>
          )}
        </div>
      </section>
      {!isStoreMode && <Footer />}
    </div>
  );
};

export default Wishlist;
